import React from 'react';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface CompactInputFieldProps {
  /** Etiqueta opcional; si se omite, se muestra solo el input. */
  label?: string;
  name: string;
  type?: 'text' | 'number' | 'email' | 'password' | 'date';
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  min?: number;
  max?: number;
  step?: string;
  placeholder?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  pattern?: string;
  rightContent?: React.ReactNode;
  ariaLabel?: string;
  /** Aplica margen inferior (mb-4). Por defecto false para usarlo en filas/grillas compactas. */
  withMargin?: boolean;
  className?: string;
}

/**
 * Versión reducida de {@link InputField}: mismos colores y comportamiento
 * (borde, foco con anillo primario, modo oscuro) pero con paddings, tipografía
 * y ancho pensados para popovers, filtros y celdas de grilla donde el input
 * grande desborda.
 */
const CompactInputField: React.FC<CompactInputFieldProps> = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  error,
  min,
  max,
  step,
  placeholder,
  iconKey,
  disabled,
  required,
  inputMode,
  pattern,
  rightContent,
  ariaLabel,
  withMargin = false,
  className = '',
}) => {
  return (
    <div className={`${withMargin ? 'mb-4' : ''} ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="mb-1.5 flex items-center gap-1 text-xs font-medium text-black dark:text-white"
        >
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div
        className={`
          bg-gray-1
          flex
          items-stretch
          overflow-hidden
          rounded-lg
          border
          ${error ? 'border-danger' : 'border-stroke'}
          transition-all
          duration-200
          focus-within:border-primary
          focus-within:ring-2
          focus-within:ring-primary/10
          dark:border-strokedark
          dark:bg-meta-4
          ${disabled ? 'cursor-not-allowed opacity-70' : ''}
        `}
      >
        {iconKey && (
          <div className="flex items-center justify-center border-r border-stroke bg-gray-2 px-2.5 text-bodydark2 dark:border-strokedark dark:bg-boxdark-2">
            <InputIconByKey iconKey={iconKey} />
          </div>
        )}

        <input
          id={name}
          name={name}
          type={type}
          value={value ?? ''}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={inputMode}
          pattern={pattern}
          aria-label={ariaLabel ?? label}
          className="w-full min-w-0 bg-transparent px-3 py-2 text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />

        {rightContent && (
          <div className="shrink-0 px-3 text-sm font-medium text-bodydark2">
            {rightContent}
          </div>
        )}
      </div>

      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </div>
  );
};

export default CompactInputField;
